import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UploadModal } from '../../components/books/UploadModal';
import { BookCard } from '../../components/books/BookCard';
import { Book } from '../../types/book';
import { useLibrary } from '../../contexts/LibraryContext';
import HeaderIconsWithDropdowns from '../../components/layout/HeaderIconsWithDropdowns';
import { uploadFile } from '../../lib/firebase';
import { useAuth } from '../../contexts/FirebaseAuthContext';

const genres = [
  'All',
  'Adventure', 'Fantasy', 'Science Fiction', 'Mystery', 'Romance', 'Non-fiction', 'Biography', 'History', 'Children', 'Other'
];

const Library: React.FC = () => {
  const navigate = useNavigate();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'my-books' | 'reading' | 'completed' | 'wishlist'>('my-books');
  const [searchQuery, setSearchQuery] = useState('');
  const { books, addBook, deleteBook, updateBook } = useLibrary();
  const [genreFilter, setGenreFilter] = useState('All');
  const { user } = useAuth();

  const handleUpload = async (book: {
    file: File | null;
    title: string;
    author: string;
    genre: string;
    cover: File | null;
    setUploadProgress: React.Dispatch<React.SetStateAction<number | null>>;
  }) => {
    try {
      if (!book.file) return;
      if (!user) {
        alert('You must be logged in to upload books.');
        return;
      }
      const bookId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const userId = user.uid;
      // Upload book file with progress
      const fileUrl = await uploadFile(book.file, userId, bookId, book.setUploadProgress);
      // Upload cover (if provided), else use default
      let coverUrl = '';
      if (book.cover) {
        coverUrl = await uploadFile(book.cover, userId, `${bookId}-cover`, book.setUploadProgress);
      } else {
        coverUrl = '/default-book-cover.png';
      }
      book.setUploadProgress(100);
      await addBook({
        title: book.title,
        author: book.author,
        genre: book.genre,
        description: '',
        coverUrl,
        fileUrl,
        fileType: book.file ? (book.file.name.split('.').pop() as any) : 'pdf',
        status: 'unread',
        progress: 0,
        addedAt: new Date().toISOString(),
        userId,
      });
    } catch (err) {
      alert('Failed to upload book. Please check your connection and permissions.');
      console.error('Upload error:', err);
    }
  };

  const handleDelete = async (bookId: string) => {
    try {
      await deleteBook(bookId);
    } catch (err) {
      alert('Failed to delete book. Please try again.');
    }
  };

  const handleEdit = (bookId: string) => {
    // TODO: Implement edit logic
    console.log('Editing book:', bookId);
  };

  const handleBookClick = async (bookId: string) => {
    try {
      // Set all other books with status 'reading' to 'unread'
      const readingBooks = books.filter(b => b.status === 'reading' && b.id !== bookId);
      await Promise.all(readingBooks.map(b => updateBook(b.id, { status: 'unread' })));
      // Set the clicked book to 'reading'
      await updateBook(bookId, { status: 'reading' });
      navigate(`/books/reading/${bookId}`);
    } catch (err) {
      alert('Failed to set book as currently reading.');
      console.error(err);
    }
  };

  // Filter books by tab, genre, and search
  const filteredBooks = books.filter(book => {
    const matchesTab = activeTab === 'my-books' ? true : activeTab === book.status;
    const matchesGenre = genreFilter === 'All' ? true : book.genre === genreFilter;
    const matchesSearch =
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesGenre && matchesSearch;
  });

  return (
    <div className="flex flex-col min-h-screen bg-[#FCFAF4]">
      {/* Header Row */}
      <div className="flex items-center justify-between mt-6 mx-8" style={{minHeight: '64px'}}>
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>My Library</h1>
          <p className="text-gray-600 text-base mt-0.5" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>All your books in one place</p>
        </div>
        <div className="flex items-center">
          <HeaderIconsWithDropdowns />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-8 pb-16">
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 mt-8">
          <input
            type="text"
            placeholder="Search in Library"
            className="w-full md:w-96 border border-gray-300 rounded-full px-5 py-2 focus:outline-none focus:ring-2 focus:ring-orange-200"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <div className="flex gap-2 items-center">
            <select
              className="border border-gray-300 rounded-full px-4 py-2 bg-white text-gray-700"
              value={genreFilter}
              onChange={e => setGenreFilter(e.target.value)}
            >
              {genres.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <button
              className="bg-black text-white rounded-full px-5 py-2 font-semibold hover:bg-gray-800 transition"
              onClick={() => setIsUploadModalOpen(true)}
            >
              + Add book
            </button>
          </div>
        </div>

        {/* Tabs */}
        <nav className="flex mb-6">
          {[
            { id: 'my-books', label: 'My Books' },
            { id: 'reading', label: 'Reading' },
            { id: 'completed', label: 'Completed' },
            { id: 'wishlist', label: 'Wishlist' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-4 border-b-2 text-sm font-medium ${
                activeTab === tab.id
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Book Grid */}
        {filteredBooks.length === 0 ? (
          <div className="text-center text-gray-500 mt-16 text-lg">No books found. Upload a book to get started!</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
            {filteredBooks.map(book => (
              <BookCard
                key={book.id}
                book={book}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onClick={() => handleBookClick(book.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />
    </div>
  );
};

export default Library; 